import { useEffect } from "react";

const SITE_URL = "https://dosoft.cl";
const DEFAULT_OG_IMAGE = "https://dosoft.cl/img/dosoft-logo.png";

const absoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

/**
 * Per-page SEO without external deps.
 * - Updates document.title and meta tags (description, OG, Twitter)
 * - Sets canonical link
 * - Injects JSON-LD scripts (tagged so they are cleaned up on route change)
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} opts.canonical   absolute or relative URL
 * @param {string} opts.image       absolute or relative URL (relative ones get prefixed with SITE_URL)
 * @param {Array<object>} opts.jsonLd  one or more JSON-LD objects to inject
 */
export default function useSeo({ title, description, canonical, image, jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
        const isProp = selector.includes("property=");
        el.setAttribute(isProp ? "property" : "name", name);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    const canonicalAbs = absoluteUrl(canonical);
    if (canonicalAbs) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalAbs);
      setMeta('meta[property="og:url"]', "content", canonicalAbs);
    }
    const imageAbs = absoluteUrl(image) || DEFAULT_OG_IMAGE;
    setMeta('meta[property="og:image"]', "content", imageAbs);
    setMeta('meta[name="twitter:image"]', "content", imageAbs);

    // JSON-LD injection (tagged for cleanup)
    const injected = [];
    if (Array.isArray(jsonLd) && jsonLd.length > 0) {
      jsonLd.forEach((data) => {
        if (!data) return;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-injected", "true");
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
        injected.push(script);
      });
    }
    return () => {
      injected.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);
}

/** Helper to build a BreadcrumbList JSON-LD from an array of {name, url} */
export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: absoluteUrl(it.url),
    })),
  };
}
